/**
 * A esteira ouve a triagem de verdade (TP-025).
 *
 * Carga inicial de `GET /captacao` (as entradas mais recentes) e depois
 * polling com `desde` = o cursor da última entrada vista. Cada entrada nova
 * vira card e é entregue à esteira em sequência, com o mesmo espaçamento das
 * notificações fabricadas, para não despejar tudo de uma vez.
 *
 * Polling manual, e não `useQuery`: cada rodada depende do cursor da anterior
 * e o resultado é um EVENTO (chegou alguém), não um estado a exibir.
 *
 * Falha não derruba a esteira mock (o real nunca quebra o protótipo) e também
 * não se esconde: vira `erro`, que a página mostra enquanto durar. O polling
 * continua e, quando a API volta, retoma do último cursor sem perder entrada.
 */

import { useEffect, useRef, useState } from 'react';

import { comoErroDaApi, type ErroDaApi } from '../../../shared/api/erros';
import type { AneryCard } from '../../../shared/ui/tiposDaEsteira';
import { buscarEntradasNaCaptacao } from './api/entradasDaCaptacao';
import { mapearEntradaParaCard } from './mapeamentoDaEntradaReal';

export interface OpcoesDaChegada {
  /** Entre uma rodada e a seguinte. */
  readonly intervaloMs?: number;
  /** Entre a entrega de um card e a do próximo, dentro de um lote. */
  readonly espacamentoMs?: number;
}

export function useChegadaDaCaptacao(
  receber: (card: AneryCard) => void,
  { intervaloMs = 5000, espacamentoMs = 1200 }: OpcoesDaChegada = {},
): { erro: ErroDaApi | null } {
  const [erro, setErro] = useState<ErroDaApi | null>(null);

  // A esteira recria `receber` quando muda; o polling não pode reiniciar por
  // isso (perderia o cursor), então lê sempre a versão mais nova daqui.
  const receberAtual = useRef(receber);
  useEffect(() => {
    receberAtual.current = receber;
  }, [receber]);

  useEffect(() => {
    let vivo = true;
    let desde: string | null = null;
    const vistos = new Set<string>();
    const timers: number[] = [];
    let controle: AbortController | null = null;
    // Quando a próxima entrega pode acontecer — um lote novo espera o
    // anterior terminar de chegar.
    let proximaEntrega = 0;

    async function rodada(): Promise<void> {
      controle = new AbortController();
      try {
        const entradas = await buscarEntradasNaCaptacao(desde, controle.signal);
        if (!vivo) return;
        setErro(null);

        for (const entrada of entradas) {
          desde = entrada.cursor;
          if (vistos.has(entrada.ticket.id)) continue;
          vistos.add(entrada.ticket.id);

          const card = mapearEntradaParaCard(entrada);
          const agora = Date.now();
          proximaEntrega = Math.max(proximaEntrega, agora);
          timers.push(
            window.setTimeout(() => {
              if (vivo) receberAtual.current(card);
            }, proximaEntrega - agora),
          );
          proximaEntrega += espacamentoMs;
        }
      } catch (causa) {
        if (!vivo) return;
        const erroDaApi = comoErroDaApi(causa);
        if (erroDaApi.origem === 'cancelado') return;
        console.warn('captacao: dado real indisponivel', erroDaApi.codigo ?? erroDaApi.origem, erroDaApi.correlationId);
        setErro(erroDaApi);
      } finally {
        if (vivo) {
          timers.push(window.setTimeout(() => void rodada(), intervaloMs));
        }
      }
    }

    void rodada();

    return () => {
      vivo = false;
      controle?.abort();
      timers.forEach(clearTimeout);
    };
  }, [intervaloMs, espacamentoMs]);

  return { erro };
}
